import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import {
 Flex,
 Text,
 Tabs,
 TabList,
 TabPanels,
 Tab,
 TabPanel,
 Breadcrumb,
 BreadcrumbItem,
 BreadcrumbLink,
 Box,
} from '@chakra-ui/react';
import { IoChevronForwardSharp } from 'react-icons/io5';
import { useParams, Link } from 'react-router-dom';
import { CompanyHeader } from '../../../components/Card';
import { useSSO } from '../../../context/SSO';
import { fetchData } from '../../../services/fetch';
import Loader from '../../../components/Feedback/Loader';
import MainContent from '../../../components/Layout/MainContent';
import PeriodicType from '../../questionnaires/PeriodicType';
import DiagnosisType from '../../questionnaires/DiagnosisType';
import Questionnaire from '../../questionnaires/Questionnaire';
import { Select } from '../../../components/Input';
import CertificationType from '../../questionnaires/CertificationType';
import CertificationContent from '../../questionnaires/certifications/CertificationContent';

const QUESTIONNAIRE_TYPES = {
 PERIODIC: 'periodic',
 DIAGNOSTIC: 'diagnostic',
 CERTIFICATION: 'certification',
};

const TAB_INDICES = {
 QUESTIONNAIRES: 0,
 CERTIFICATIONS: 1,
};

export default function SurveyAnalysisView() {
 const { id: companyId } = useParams();
 const { user } = useSSO();

 const [selectedCompanyId, setSelectedCompanyId] = useState(companyId);
 const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState(null);
 const [selectedCertificationId, setSelectedCertificationId] = useState(null);
 const [selectedUserId, setSelectedUserId] = useState(null);
 const [activeTab, setActiveTab] = useState(TAB_INDICES.QUESTIONNAIRES);

 const {
  data: companyData,
  error: companyError,
  isLoading: isCompanyLoading,
 } = useSWR(
  user?.user?.id_user ? `company/admin_company/${user?.user?.id_user}` : null,
  fetchData
 );

 const {
  data: formsList,
  error: formsError,
  isLoading: isFormsLoading,
 } = useSWR(companyId ? `/company/linkSurveyCompany/${companyId}` : null, fetchData);

 const { data: certificationsList } = useSWR(
  user?.user?.company_group
   ? `certificate/link-group/${user?.user?.company_group}`
   : null,
  fetchData
 );

 const { data: users } = useSWR(
  user?.user?.company_group ? `user?group=${user?.user?.company_group}` : null,
  fetchData
 );

 const transformedCompanyData = useMemo(() => {
  if (!companyData) return [];

  return companyData.map((company) => ({
   c_id_company: company.id_company,
   c_name: company.name,
   c_cnpj: company.cnpj,
   c_fantasyname: company.fantasyname,
   c_address: company.address,
   c_phone: company.phone,
  }));
 }, [companyData]);

 const userOptions = useMemo(() => {
  if (!users?.length) return [];

  const respondentUsers = users.filter((user) =>
   user.roles.some((role) => role.name === 'RESPONDENTE')
  );

  return respondentUsers.map((user) => ({
   value: user.id_user,
   label: user.username,
  }));
 }, [users]);

 const surveys = useMemo(() => {
  if (!companyData) return [];

  const currentCompany = companyData.find(
   (company) => company.id_company === Number(companyId)
  );

  return currentCompany?.surveys || [];
 }, [companyData, companyId]);

 const matchedQuestionnaires = useMemo(() => {
  if (!formsList?.length || !surveys?.length) return [];

  const surveyIds = new Set(surveys.map((survey) => survey.id_survey));
  return formsList.filter((form) => surveyIds.has(form.s_id_survey));
 }, [formsList, surveys]);

 const questionnaireOptions = useMemo(() => {
  if (!matchedQuestionnaires?.length) return [];

  return matchedQuestionnaires.map((questionnaire) => ({
   value: questionnaire.s_id_survey,
   label: questionnaire.s_description,
  }));
 }, [matchedQuestionnaires]);

 const selectedQuestionnaire = useMemo(
  () =>
   matchedQuestionnaires?.find(
    (questionnaire) => questionnaire.s_id_survey === selectedQuestionnaireId
   ),
  [matchedQuestionnaires, selectedQuestionnaireId]
 );

 const certificationOptions = useMemo(() => {
  if (!certificationsList?.length) return [];

  return certificationsList.map((certification) => ({
   value: certification.id_certificate,
   label: certification.name,
  }));
 }, [certificationsList]);

 const handleQuestionnaireChange = useCallback((e) => {
  setSelectedQuestionnaireId(Number(e.target.value));
 }, []);

 const handleCertificationChange = (e) => {
  setSelectedCertificationId(Number(e.target.value));
 };

 const handleUserChange = useCallback((e) => {
  setSelectedUserId(Number(e.target.value));
 }, []);

 const handleTabChange = useCallback((index) => {
  setActiveTab(index);
 }, []);

 useEffect(() => {
  if (matchedQuestionnaires?.length > 0 && !selectedQuestionnaireId) {
   setSelectedQuestionnaireId(matchedQuestionnaires[0].s_id_survey);
  }
 }, [matchedQuestionnaires, selectedQuestionnaireId]);

 useEffect(() => {
  if (certificationsList?.length > 0 && !selectedCertificationId) {
   setSelectedCertificationId(certificationsList[0].id_certificate);
  }
 }, [certificationsList, selectedCertificationId]);

 useEffect(() => {
  if (userOptions?.length > 0 && !selectedUserId) {
   setSelectedUserId(userOptions[0].value);
  }
 }, [userOptions, selectedUserId]);

 const selectedCertification = useMemo(
  () =>
   certificationsList?.find((cert) => cert.id_certificate === selectedCertificationId),
  [certificationsList, selectedCertificationId]
 );

 const renderQuestionnaireContent = useMemo(() => {
  if (!selectedQuestionnaire) return null;

  const commonProps = {
   companyId: selectedCompanyId,
   surveyId: selectedQuestionnaire.s_id_survey,
   companyGroupId: companyData?.idCompanyGroup,
   formName: selectedQuestionnaire.s_description,
   selectedCompanyId,
   isResponsibleUser: true,
  };

  const contentMap = {
   [QUESTIONNAIRE_TYPES.PERIODIC]: <PeriodicType {...commonProps} />,
   [QUESTIONNAIRE_TYPES.DIAGNOSTIC]: <DiagnosisType {...commonProps} />,
   [QUESTIONNAIRE_TYPES.CERTIFICATION]: (
    <CertificationType {...commonProps} hasPillars={selectedQuestionnaire?.has_pillar} />
   ),
  };

  return contentMap[selectedQuestionnaire.s_type] || <Questionnaire {...commonProps} />;
 }, [selectedQuestionnaire, selectedCompanyId, companyData, user]);

 if (isCompanyLoading || isFormsLoading) {
  return <Loader />;
 }

 if (companyError || formsError) {
  return (
   <Flex p='4' justify='center'>
    <Text fontWeight='500'>Erro ao carregar dados. Por favor, tente novamente.</Text>
   </Flex>
  );
 }

 const hasNoSurveys = surveys?.length === 0;

 return (
  <MainContent large minH='90vh'>
   <Breadcrumb spacing='8px' mb='24px' separator={<IoChevronForwardSharp />}>
    <BreadcrumbItem>
     <BreadcrumbLink as={Link} to='/survey-analysis' textDecor='underline'>
      Revisão
     </BreadcrumbLink>
    </BreadcrumbItem>

    <BreadcrumbItem>
     <BreadcrumbLink href='#' isCurrentPage fontWeight={700}>
      Revisar respostas
     </BreadcrumbLink>
    </BreadcrumbItem>
   </Breadcrumb>

   <CompanyHeader
    companies={transformedCompanyData}
    selectedCompanyId={selectedCompanyId}
    setSelectedCompanyId={setSelectedCompanyId}
    removeEditButton
   />

   {hasNoSurveys ? (
    <Flex p='52px 32px'>
     <Text fontSize='15px' fontWeight={500}>
      Não existem questionários vinculados a essa empresa.
     </Text>
    </Flex>
   ) : (
    <Tabs size='md' variant='primary' mt='30px' onChange={handleTabChange}>
     <Box p='24px 32px 29px 32px' bg='white' borderRadius='12px' mb='6'>
      <TabList mb='21.5px'>
       <Tab>Questionários</Tab>
       <Tab>Certificações</Tab>
      </TabList>

      {activeTab === TAB_INDICES.QUESTIONNAIRES &&
       questionnaireOptions?.length > 0 &&
       selectedQuestionnaireId && (
        <Flex gap='6' justify='left'>
         <Flex w='300px'>
          <Select
           value={selectedQuestionnaireId}
           onChange={handleQuestionnaireChange}
           options={questionnaireOptions}
          />
         </Flex>
        </Flex>
       )}

      {activeTab === TAB_INDICES.CERTIFICATIONS &&
       certificationOptions?.length > 0 &&
       selectedCertificationId && (
        <Flex gap='6' justify='left'>
         <Flex w='300px'>
          <Select
           defaultValue={selectedCertificationId || ''}
           value={selectedCertificationId || ''}
           onChange={handleCertificationChange}
           options={certificationOptions}
          />
         </Flex>
        </Flex>
       )}
     </Box>
     <TabPanels>
      <TabPanel p={0}>
       {formsList?.length === 0 ? (
        <Flex p='32px'>
         <Text fontSize='15px' fontWeight={500}>
          Não existem questionários vinculados a essa empresa.
         </Text>
        </Flex>
       ) : (
        <Box>{renderQuestionnaireContent}</Box>
       )}
      </TabPanel>

      <TabPanel p={0}>
       {certificationsList?.length === 0 ? (
        <Flex p='32px'>
         <Text fontSize='15px' fontWeight={500}>
          Não existem certificações vinculadas a essa empresa.
         </Text>
        </Flex>
       ) : (
        <Box>
         <CertificationContent
          selectedCertificationId={selectedCertificationId}
          hasPillars={selectedCertification?.has_pillar}
          selectedCompanyId={selectedCompanyId}
          surveyId={selectedCertificationId}
          companyGroupId={companyData?.idCompanyGroup || user?.user?.company_group}
          isResponsibleUser={true}
         />
        </Box>
       )}
      </TabPanel>
     </TabPanels>
    </Tabs>
   )}
  </MainContent>
 );
}
